// src/api/dashboardApi.ts
import { getAuthToken } from "../security/AuthLogin";

const BASE_URL = `${window.location.protocol}//${window.location.hostname}`;
const API_URL = `${BASE_URL}/api`;

/* -------------------------------------------------
   Tipos e interfaces
-------------------------------------------------- */
export type Period = "annual" | "monthly" | "total";

export interface DashboardChartResponse {
  success: boolean;
  labels: (string | number)[];
  data: number[];
}

export interface ClienteSimple {
  id_usuario: string;
  nombre: string;
}

export interface FavoritoUrgente {
  id_usuario: string;
  empresa: string;
  cliente_nombre: string;
  id_concurso: string;
  titulo_concurso: string;
  codigo_expediente: string;
  fecha_fin_ofertas?: string;
  estado_favorito?: string;
  dias_restantes?: number;
  soportes?: {
    id_soporte: number;
    id_tipo_soporte: number;
  }[];
  observaciones?: string;
}

export interface CalendarioDetalle {
  id_concurso: string;
  codigo_expediente: string;
  importe: number | null;
}

export interface FacturacionAnualResponse {
  success: boolean;
  labels: number[]; // 1-12
  data: number[]; // importes €
  prevTotal: number; // total año anterior
  prevYear: number; // nº año anterior
}

export interface MapaProvincia {
  codigo_nut: string | null; // puede ser null; lo filtraremos en el front
  provincia?: string; // opcional porque a veces llega solo 'localidad'
  localidad?: string; // idem
  pais: "ES" | "PT";
  total: number;
}

/* ------------------ */
/*   CLIENTE Methods  */
/* ------------------ */

/** GET /api/dashboard/cliente/concursos-presentados?period=... */
export async function getConcursosPresentados(
  period: Period
): Promise<DashboardChartResponse> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("Token no encontrado.");
  }

  const resp = await fetch(
    `${API_URL}/dashboard/cliente/concursos-presentados?period=${period}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!resp.ok) {
    throw new Error(`Error getConcursosPresentados: ${await resp.text()}`);
  }

  const json = await resp.json();
  return {
    success: !!json.success,
    labels: json.labels ?? [],
    data: json.data ?? [],
  };
}

/** GET /api/dashboard/cliente/adjudicaciones-presentadas?period=... */
export async function getAdjudicacionesPresentadas(
  period: Period
): Promise<DashboardChartResponse> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(
    `${API_URL}/dashboard/cliente/adjudicaciones-presentadas?period=${period}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!resp.ok) {
    throw new Error(`Error getAdjudicacionesPresentadas: ${await resp.text()}`);
  }

  const json = await resp.json();
  return {
    success: !!json.success,
    labels: json.labels ?? [],
    data: json.data ?? [],
  };
}

/** GET /api/dashboard/cliente/favoritos-viables-noPresentado */
export async function getFavoritosViablesNoPresentado(): Promise<number> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(
    `${API_URL}/dashboard/cliente/favoritos-viables-noPresentado`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!resp.ok) {
    throw new Error(
      `Error getFavoritosViablesNoPresentado: ${await resp.text()}`
    );
  }

  const json = await resp.json();
  return json.count ?? 0;
}

/** GET /api/dashboard/cliente/concursos-en-plazo */
export async function getConcursosEnPlazo(): Promise<number> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(`${API_URL}/dashboard/cliente/concursos-en-plazo`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!resp.ok) {
    throw new Error(`Error getConcursosEnPlazo: ${await resp.text()}`);
  }

  const json = await resp.json();
  return json.count ?? 0;
}

/** GET /api/dashboard/cliente/presentados-vs-total-adjudicaciones */
export async function getPresentadosVsTotalAdjudicaciones(): Promise<{
  presentados: number;
  ganadas: number;
}> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(
    `${API_URL}/dashboard/cliente/presentados-vs-total-adjudicaciones`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!resp.ok) {
    throw new Error(
      `Error getPresentadosVsTotalAdjudicaciones: ${await resp.text()}`
    );
  }

  const json = await resp.json();
  return {
    presentados: json.presentados ?? 0,
    ganadas: json.ganadas ?? 0,
  };
}

/** GET /api/dashboard/media-numero-licitadores */
export async function getMediaNumeroLicitadores(): Promise<number> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(`${API_URL}/dashboard/media-numero-licitadores`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!resp.ok) {
    throw new Error(`Error getMediaNumeroLicitadores: ${await resp.text()}`);
  }

  const json = await resp.json();
  return json.media ?? 0;
}

/** GET /api/dashboard/cliente/facturacion-anual */
export async function getFacturacionAnual(): Promise<FacturacionAnualResponse> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(`${API_URL}/dashboard/cliente/facturacion-anual`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!resp.ok) {
    throw new Error(`Error getFacturacionAnual: ${await resp.text()}`);
  }

  return (await resp.json()) as FacturacionAnualResponse;
}

/** GET /api/dashboard/cliente/mapa-provincias */
export async function getMapaProvincias(): Promise<MapaProvincia[]> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(`${API_URL}/dashboard/cliente/mapa-provincias`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!resp.ok) {
    throw new Error(`Error getMapaProvincias: ${await resp.text()}`);
  }

  const json = await resp.json();
  return json.data ?? [];
}

/* ----------------------------------- */
/*        ADMIN / TECNICO Methods      */
/* ----------------------------------- */

/** GET /api/usuarios/clientes-simples */
export async function getClientesSimples(): Promise<ClienteSimple[]> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(`${API_URL}/usuarios/clientes-simples`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!resp.ok) {
    throw new Error(`Error getClientesSimples: ${await resp.text()}`);
  }

  const json = await resp.json();
  if (!json.success || !Array.isArray(json.clientes)) return [];
  return json.clientes;
}

/** GET /api/dashboard/admtecnico/favoritos-urgentes */
export async function getFavoritosUrgentes(): Promise<FavoritoUrgente[]> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(
    `${API_URL}/dashboard/admtecnico/favoritos-urgentes`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!resp.ok) {
    throw new Error(`Error getFavoritosUrgentes: ${await resp.text()}`);
  }

  const json = await resp.json();
  if (!json.success || !Array.isArray(json.favoritos)) return [];
  return json.favoritos;
}

/* ---------- Calendario ---------- */

/** GET /api/dashboard/cliente/calendario-favoritos/fechas */
export async function getCalendarioFechas(): Promise<string[]> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(
    `${API_URL}/dashboard/cliente/calendario-favoritos/fechas`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!resp.ok) {
    throw new Error(`Error getCalendarioFechas: ${await resp.text()}`);
  }

  const json = await resp.json();
  return json.fechas ?? [];
}

/** GET /api/dashboard/cliente/calendario-favoritos/detalle?fecha=YYYY-MM-DD */
export async function getCalendarioDetalle(
  fecha: string
): Promise<CalendarioDetalle[]> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(
    `${API_URL}/dashboard/cliente/calendario-favoritos/detalle?fecha=${fecha}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!resp.ok) {
    throw new Error(`Error getCalendarioDetalle: ${await resp.text()}`);
  }

  const json = await resp.json();
  return json.concursos ?? [];
}

/* ---------- Funciones auxiliares ---------- */

/** Actualizar observaciones de favoritos */
export async function updateFavoritoObservaciones(
  id_usuario: string,
  id_concurso: string,
  observaciones: string
): Promise<boolean> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(
    `${API_URL}/dashboard/admtecnico/favorito-observaciones`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ id_usuario, id_concurso, observaciones }),
    }
  );

  if (!resp.ok) {
    throw new Error(`Error updateFavoritoObservaciones: ${await resp.text()}`);
  }

  const json = await resp.json();
  return json.success ?? false;
}

/** Actualizar estado presentado de favorito */
export async function updateFavoritoPresentado(
  id_concurso: string,
  id_usuario: string,
  presentado: number
): Promise<boolean> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(
    `${API_URL}/favoritos/presentado/${id_concurso}/${id_usuario}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ presentado }),
    }
  );

  if (!resp.ok) {
    throw new Error(`Error updateFavoritoPresentado: ${await resp.text()}`);
  }

  const json = await resp.json();
  return json.success ?? false;
}

/** Verificar si existe un soporte */
export async function verificarSoporteExiste(
  tipo: string,
  id_concurso: string,
  id_usuario: string
): Promise<{ exists: boolean; id_soporte: number | null }> {
  const token = getAuthToken();
  if (!token) {
    window.location.href = "/login";
    throw new Error("No token.");
  }

  const resp = await fetch(
    `${API_URL}/soporte-existe?tipo=${tipo}&id_concurso=${id_concurso}&id_usuario=${id_usuario}`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!resp.ok) {
    throw new Error(`Error verificarSoporteExiste: ${await resp.text()}`);
  }

  const json = await resp.json();
  return {
    exists: json.exists ?? false,
    id_soporte: json.id_soporte ?? null,
  };
}
